import { ref, watch } from "@vue/composition-api";
import { Call, CallCreateResponse, CallStatus, CallType } from "@scrile/api-provider/dist/api/CallsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import useAuthUser from "@/hooks/useAuthUser";
import useDoNotDisturb from "@/hooks/useDoNotDisturb";
import providers from "@/lib/api";
import AudioPlayer from "@/lib/audioPlayer";
import { isMobile } from "@/lib/browserUtils";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import CallsLogger, { LoggerContextWithMessage } from "@/modules/callsLogger";
import { PreventLogout } from "@/types";
import { connectionStateEnum, EVENTS, PreventLogoutType } from "@/const";
import { pauseGlobalPlayer } from "@/components/GlobalAudioPlayer/helpers";

const ERROR_DISPLAY_TIME = 6000;
const DEFAULT_STATS_RECORDS = 20;

type ProducersUpdatedData = {
  video: boolean;
  audio: boolean;
  screen: boolean;
};

export default function useCalls() {
  const { authUserStrict } = useAuthUser();
  const { doNotDisturb, setDoNotDisturb } = useDoNotDisturb();

  let WebRTC: StreamingHelper;
  const call = ref<Call | null>(null);
  const callConnection = ref<WebRTCConnection | null>(null);
  const opponentUser = ref<UserPublicData | null>(null);
  const showNotification = ref(false);
  const showConnectionError = ref(false);
  const connectionErrorMessage = ref("");
  let errorTimeout: number | NodeJS.Timeout = 0;
  const opponentSubscriptionStatus = ref<"follower" | "subscriber" | "user">("user");
  const opponentStream = ref<MediaStream>(new MediaStream([]));
  const localStream = ref<MediaStream>(new MediaStream([]));
  const localReadyVideoState = ref(false);
  const opponentReadyVideoState = ref(false);
  const localVideoState = ref(false);
  const opponentVideoState = ref(false);
  const loading = ref(false);
  const connectingState = ref<connectionStateEnum>(connectionStateEnum.EMPTY);
  const isAccepted = ref(false);

  const getWebRTCLoggingContext = () => {
    return {
      consumer: {
        roomId: (WebRTC.webRTCData?.consumer.client as any)?.roomId,
        userId: (WebRTC.webRTCData?.consumer.client as any)?.userId,
      },
      producer: {
        roomId: (WebRTC.webRTCData?.producer.client as any)?.roomId,
        userId: (WebRTC.webRTCData?.producer.client as any)?.userId,
      },
      stats: WebRTC.getStatisticHistory(DEFAULT_STATS_RECORDS),
      info: {
        callId: call.value?.id,
        time: new Date().getTime(),
      },
    };
  };

  const collectErrorLogs = (eventName: string, error: Error, context?: Record<string, any>) =>
    CallsLogger.collectLogs(`[CAll CONTROLLER]:ERROR - ${eventName}`, {
      ...getWebRTCLoggingContext(),
      ...context,
      streaming: true,
      error,
    });

  const businessLogicLog = (logMessage: string, context?: Record<string, any>) =>
    CallsLogger.collectLogs(`[CAll CONTROLLER] - ${logMessage}`, {
      call: call.value,
      connection: callConnection.value,
      ...getWebRTCLoggingContext(),
      ...context,
      streaming: true,
    });

  const onLocalStreamChanged = () => {
    localVideoState.value = localStream.value.getVideoTracks().length > 0;
  };

  const onOpponentStreamChanged = () => {
    opponentVideoState.value = opponentStream.value.getVideoTracks().length > 0;
  };

  const closeTracks = () => {
    opponentStream.value.getTracks().forEach(track => track.stop());
    localStream.value.getTracks().forEach(track => track.stop());
    opponentStream.value = new MediaStream();
    localStream.value = new MediaStream();
    onOpponentStreamChanged();
    onLocalStreamChanged();
  };

  const resetLocalState = (immediate: boolean = false) => {
    opponentReadyVideoState.value = false;
    localReadyVideoState.value = false;
    callConnection.value = null;
    showConnectionError.value = false;
    connectionErrorMessage.value = "";
    isAccepted.value = false;
    if (errorTimeout !== 0) {
      clearTimeout(errorTimeout as number);
      errorTimeout = 0;
    }
    const clear = () => {
      call.value = null;
      opponentUser.value = null;
      showNotification.value = false;
      connectingState.value = connectionStateEnum.EMPTY;
    };
    if (immediate) clear();
    else setTimeout(() => clear(), 2000);
  };

  const callEnd = (callId: string) => {
    setDoNotDisturb(false);
    providers.CallsProvider.setReceiveErrors(false);
    businessLogicLog("Call end");
    emitter.emit(EVENTS.PREVENT_LOGOUT, { value: false, type: PreventLogoutType.CALL_CONTROLLER });
    return providers.CallsProvider.end({ callId });
  };

  const reset = async (immediate: boolean = false, ignore: boolean = false) => {
    if (!ignore && call.value) call.value = await callEnd(call.value.id);
    closeTracks();
    WebRTC.closeAllStreams();
    resetLocalState(immediate);
  };

  const connectionErrorCb = async () => {
    showConnectionError.value = true;
    errorTimeout = setTimeout(() => resetLocalState(), ERROR_DISPLAY_TIME);
    closeTracks();
    WebRTC.closeAllStreams();
    if (call.value) call.value = await callEnd(call.value.id);
  };

  const getStream = async (video: MediaStreamConstraints["video"], audio: MediaStreamConstraints["audio"]) => {
    try {
      return await WebRTC.getStream({ video, audio });
    } catch (error) {
      collectErrorLogs("Getting stream", error as Error);
      connectionErrorMessage.value = video
        ? $t("Please allow the site access to the camera in your Browser settings menu.")
        : $t("Please allow the site access to the camera and microphone in your Browser settings menu.");
      throw error;
    }
  };

  const getDevicePermission = async ({ video, audio }: MediaStreamConstraints) => {
    (await getStream(video, audio)).getTracks().forEach(track => track.stop());
  };

  const produceLocalStream = async (
    connection: WebRTCConnection,
    { video, audio }: MediaStreamConstraints,
    shouldCreateStream = true
  ) => {
    localStream.value.removeEventListener("addtrack", onLocalStreamChanged);
    localStream.value.removeEventListener("removetrack", onLocalStreamChanged);
    localStream.value = await WebRTC.produceStream(
      connection,
      shouldCreateStream ? await getStream(video, audio) : { video, audio }
    );
    onLocalStreamChanged();
    localStream.value.addEventListener("addtrack", onLocalStreamChanged);
    localStream.value.addEventListener("removetrack", onLocalStreamChanged);
  };

  const consumeOpponentStream = async (
    connection: WebRTCConnection,
    { video, audio }: { video: boolean; audio: boolean },
    onData: (data: ProducersUpdatedData) => void
  ) => {
    opponentStream.value.removeEventListener("addtrack", onOpponentStreamChanged);
    opponentStream.value.removeEventListener("removetrack", onOpponentStreamChanged);
    //this.opponentStream is not reactivity
    opponentStream.value = await WebRTC.consumeStream({
      connection,
      tracks: { video, audio },
      onData,
    });
    onOpponentStreamChanged();
    opponentStream.value.addEventListener("addtrack", onOpponentStreamChanged);
    opponentStream.value.addEventListener("removetrack", onOpponentStreamChanged);
  };

  const setVideoState = async (status: boolean) => {
    if (!callConnection.value) return;

    loading.value = true;
    if (!status) {
      opponentReadyVideoState.value = status;
    }
    localReadyVideoState.value = status;
    WebRTC.webRTCData?.producer.client?.sendData({ video: status });

    try {
      businessLogicLog("Set video", { video: status, audio: true });
      await produceLocalStream(callConnection.value, { video: status, audio: true });
    } catch (error) {
      collectErrorLogs("Set video state", error as Error, { video: status, audio: true });
      WebRTC.webRTCData?.producer.client?.sendData({ videoState: false });
      // eslint-disable-next-line no-console
      console.warn(error);
    } finally {
      loading.value = false;
    }
  };

  const changeOpponentVideoState = async ({ video }: { video: ProducersUpdatedData["video"] }) => {
    opponentReadyVideoState.value = video;
    try {
      if (callConnection.value) {
        await consumeOpponentStream(
          callConnection.value,
          { video: opponentReadyVideoState.value, audio: true },
          changeOpponentVideoState
        );
      }
      if (!video && localReadyVideoState.value) {
        await setVideoState(false);
      }
    } catch (error) {
      collectErrorLogs("Change opponent video state", error as Error);
      throw error;
    }
  };

  const callCreate = async (data: { receiverId: string; subject: Subject<CallType> }) => {
    setDoNotDisturb(true);
    try {
      providers.CallsProvider.setReceiveErrors(false);
      businessLogicLog("Call created");
      return await providers.CallsProvider.create({ data });
    } catch (error) {
      collectErrorLogs("Call created", error as Error);
      setDoNotDisturb(false);
      throw error;
    }
  };

  const callAccept = async (callId: string) => {
    setDoNotDisturb(true);
    try {
      loading.value = true;
      providers.CallsProvider.setReceiveErrors(false);
      isAccepted.value = true;
      businessLogicLog("Call acceptation");
      return await providers.CallsProvider.accept({ callId });
    } catch (error) {
      collectErrorLogs("Call acceptation", error as Error);
      setDoNotDisturb(true);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const preventLogout: PreventLogout = {
    value: true,
    type: PreventLogoutType.CALL_CONTROLLER,
    cb: async () => {
      await reset(true);
    },
  };

  const callOutgoingHandler = async (data: { subject?: Appointment; opponentUser: UserPublicData }) => {
    opponentUser.value = data.opponentUser;
    try {
      loading.value = true;
      await getDevicePermission({ audio: true });
      const { call: newCall, connection } = await callCreate({
        receiverId: data.opponentUser.id,
        subject: {
          subjectId: !!data.subject ? data.subject.id : "0",
          subjectType: !!data.subject ? CallType.APPOINTMENT : CallType.CALL,
        },
      });
      call.value = newCall;
      callConnection.value = connection;
      const context = { call: call.value, connection: callConnection.value };
      businessLogicLog("Setup outgoing call");
      CallsLogger.addBreadcrumb("Setup outgoing call", context);
      emitter.emit(EVENTS.PREVENT_LOGOUT, preventLogout);

      await produceLocalStream(callConnection.value, { video: false, audio: true });
    } catch (error) {
      collectErrorLogs("callOutgoingHandler", error as Error);
      await connectionErrorCb();
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const callUpdateHandler = async (newCall: Call) => {
    if (call.value?.id !== newCall.id || showConnectionError.value) return;
    call.value = newCall;
    if (call.value.status === CallStatus.ACCEPTED && callConnection.value) {
      if (!isAccepted.value && call.value.receiverId === authUserStrict.value.id) {
        businessLogicLog("Update call. Hide Notification. Call was accepted by other place");
        reset(false, true);
        return;
      }
      try {
        loading.value = true;
        businessLogicLog("Update call");
        await consumeOpponentStream(callConnection.value, { video: false, audio: true }, changeOpponentVideoState);
      } catch (error) {
        collectErrorLogs("Call updating", error as Error);
        throw error;
      } finally {
        loading.value = false;
      }
    } else {
      const ignore = call.value.status === CallStatus.DECLINED && call.value.receiverId === authUserStrict.value.id;
      reset(false, ignore);
    }
  };

  const callIncomingHandler = async (callCreateResponse: CallCreateResponse) => {
    if (!call.value && !doNotDisturb.value) {
      showNotification.value = true;
      opponentUser.value = callCreateResponse.call.caller;
      call.value = callCreateResponse.call;
      callConnection.value = callCreateResponse.connection;
      const context = { call: call.value, connection: callConnection.value };
      businessLogicLog("Setup incoming call");
      CallsLogger.addBreadcrumb("Setup incoming call", context);

      const subscriberResult = await providers.SubscriptionProvider.search({
        data: { consumerIds: [callCreateResponse.call.callerId], providerId: callCreateResponse.call.receiverId },
      });
      if (subscriberResult.result.length > 0) {
        opponentSubscriptionStatus.value = "subscriber";
      } else {
        const followerResult = await providers.FollowingsProvider.find({
          data: {
            userId: callCreateResponse.call.receiverId,
            followerId: callCreateResponse.call.callerId,
          },
        });
        if (followerResult.result.length > 0) {
          opponentSubscriptionStatus.value = "follower";
        }
      }
    }
  };

  const handleCallAccept = async () => {
    showNotification.value = false;
    if (call.value && callConnection.value) {
      try {
        loading.value = true;
        await getDevicePermission({ audio: true });
        call.value = await callAccept(call.value.id);
        await produceLocalStream(callConnection.value, { video: false, audio: true });
        await consumeOpponentStream(callConnection.value, { video: false, audio: true }, changeOpponentVideoState);
        emitter.emit(EVENTS.PREVENT_LOGOUT, preventLogout);
      } catch (error) {
        collectErrorLogs("Call accepting", error as Error);
        await connectionErrorCb();
        throw error;
      } finally {
        loading.value = false;
      }
    }
  };

  const handleCallBack = () => {
    businessLogicLog("Call back");
    const user = opponentUser.value;
    reset(true, true);
    emitter.emit(EVENTS.CALL_OUTGOING, { opponentUser: user });
  };

  const onChangeDevice = async (trackData: MediaTrackConstraints) => {
    businessLogicLog("Change device");
    if (!callConnection.value) return;
    if (isMobile()) {
      localStream.value.getVideoTracks().forEach(t => {
        t.stop();
        localStream.value.removeTrack(t);
      });
    }

    try {
      loading.value = true;
      await produceLocalStream(callConnection.value, { video: trackData, audio: true }, false);
    } catch (error) {
      collectErrorLogs("Change device", error as Error);
    } finally {
      loading.value = false;
    }
  };

  const init = () => {
    WebRTC = new StreamingHelper({
      consumerOnAllStreamClosed: () => {
        collectErrorLogs("Consumer all stream closed", new Error("All stream closed"));
        connectionErrorCb();
      },
    });
    WebRTC.on("close", e => {
      const error = (e.data as Error | undefined) ?? new Error("Client closed");
      collectErrorLogs("WebRTC Event close", error, { type: e.type });
    });
    WebRTC.on("producers-updated", changeOpponentVideoState);
    WebRTC.on("connectionstatechange", (state: connectionStateEnum) => {
      if (call.value?.status === CallStatus.NEW) return;
      connectingState.value = state;
    });
    WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) => {
      CallsLogger.collectLogs(`[STREAM CLIENT] ${message}`, { streaming: true, ...others });
    });

    window.addEventListener("beforeunload", () => {
      if (
        call.value &&
        (call.value.status === CallStatus.ACCEPTED || call.value.callerId === authUserStrict.value.id)
      ) {
        businessLogicLog("window was closed");
        reset();
      }
      return "";
    });
  };

  const toggleBeeping = (call: Call | null) => {
    if (call?.status === CallStatus.NEW && call?.callerId === authUserStrict.value.id) {
      try {
        AudioPlayer.play("beep", { loop: true });
      } catch (error) {
        collectErrorLogs("Watch call", error as Error);
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    } else {
      AudioPlayer.stop("beep");
    }
  };

  const checkStoppingGlobalPlayer = (call: Call | null, old: Call | null) => {
    if (call && !old) {
      pauseGlobalPlayer();
    }
  };

  watch(call, (call, old) => {
    checkStoppingGlobalPlayer(call, old);
    toggleBeeping(call);
  });

  return {
    opponentUser,
    showNotification,
    call,
    loading,
    opponentStream,
    localStream,
    localReadyVideoState,
    opponentReadyVideoState,
    showConnectionError,
    connectionErrorMessage,
    localVideoState,
    opponentVideoState,
    connectingState,
    opponentSubscriptionStatus,
    setVideoState,
    reset,
    handleCallBack,
    callCreate,
    callOutgoingHandler,
    callIncomingHandler,
    onChangeDevice,
    handleCallAccept,
    callUpdateHandler,
    collectErrorLogs,
    init,
  };
}
